import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import BadgeList from 'dpl/common/components/BadgeList';
import BreederProfileDeliveryOptions from 'dpl/components/BreederProfile/Show/BreederProfileDeliveryOptions';
import BreederProfileFamilyOverlay, {
  BreederProfileFamilyOverlayDetailsSection,
  BreederProfileFamilyOverlayDetailsSectionItems,
  BreederProfileFamilyOverlayTree,
  imageDimensions
} from 'dpl/components/BreederProfile/Show/BreederProfileFamilyOverlay';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import MediaCarousel from 'dpl/components/MediaCarousel';
import ImageCarouselImageCaption from 'dpl/components/MediaCarousel/ImageCarouselImageCaption';
import { ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import { ANALYTICS } from 'dpl/constants/query_params';
import ModalApplyButton from 'dpl/dog_modals/components/ModalApplyButton';
import { capitalize, groupBy } from 'dpl/shared/utils';
import { LITTER_STATES } from 'dpl/shared/constants/shared';

import useLitterModal from '../hooks/useLitterModal';

const { BORN, EXPECTED, PLANNED } = LITTER_STATES;

export function formatPrefList(preferences) {
  return preferences?.map(p => p.description).join(', ');
}

/** @param {any} props */
export default function LitterModal({ litterId = null, onClose = null }) {
  const {
    litter,
    litterImages,
    included,
    deliveryMethodsData,
    isSuccess,
    isLitterModalOpen,
    onPuppyClick,
    onModalClose
  } = useLitterModal({
    litterId,
    onClose
  });

  const {
    breed_name: breedName,
    breed_id: breedId,
    breeder_profile: breederProfile,
    description_with_intro: description,
    parents,
    price_range: price,
    puppies,
    status,
    snoozed,
    transportation
  } = litter;

  const { queryParams } = useSelector(state => state);
  const reserved = Boolean(puppies.length && puppies.every(p => p.reserved));

  function getTransportDescription() {
    let transportDescription = transportation.pick_up_sentence;

    if (transportation.drop_off_sentence) {
      transportDescription = transportDescription
        ? `${transportDescription}. ${transportation.drop_off_sentence}`
        : transportation.drop_off_sentence;
    }

    return `${transportDescription}.`;
  }

  let title = '';
  let details = [
    ['Breed', breedName],
    ['Price', price],
    ['Location', getTransportDescription()]
  ];
  const preferences = [
    ['Generation', formatPrefList(litter.generation_preferences_attributes)],
    ['Color', formatPrefList(litter.color_preferences_attributes)],
    ['Size', formatPrefList(litter.size_preferences_attributes)],
    ['Coat type', formatPrefList(litter.coat_type_preferences_attributes)]
  ];

  if (status === BORN) {
    const gendersCount = Object.entries(groupBy(puppies, 'gender'))
      .map(([gender, genderedPuppies]) => {
        const { length } = genderedPuppies;
        const genderUnit =
          length === 1 ? capitalize(gender) : `${capitalize(gender)}s`;
        return `${length} ${genderUnit}`;
      })
      .join(', ');

    title = `Newborn ${breedName} Litter`;
    details = [
      ...details,
      ['Genders', gendersCount],
      ['Age', litter.age_description],
      ['Ready Date', litter.full_go_home_date],
      ...preferences
    ];
  } else if (status === EXPECTED) {
    title = `Expected ${breedName} Litter`;
    details = [
      ...details,
      ['Expected Date', litter.full_birth_date],
      ['Ready Date', `${litter.full_go_home_date} (Approximately)`],
      ...preferences
    ];
  } else if (status === PLANNED) {
    title = `Planned ${breedName} Litter`;
    details = [
      ...details,
      ['Expected Date', litter.full_birth_date],
      ...preferences
    ];
  }

  const analytics = {
    ...queryParams[ANALYTICS],
    breeder_preferred_status: breederProfile?.preferred_qualification?.status,
    view: ANALYTICS_VIEWS.LITTER_MODAL
  };

  if (!isLitterModalOpen) {
    return null;
  }

  return (
    <BreederProfileFamilyOverlay
      onClose={onModalClose}
      contentClassName="flex flex-column relative overflow-y-hidden"
      scrollableContent
    >
      <div className="BreederProfileLitterOverlay flex-auto overflow-y-auto no-scrollbars max-h-100 dpl-fixed-scroll-wrapper-opt-out">
        <LoadingWrapper isLoading={!isSuccess} className="w-100 h-100">
          {() => (
            <>
              <div
                className="BreederProfileFamilyOverlayHeader"
                data-test-id="BreederProfileFamilyOverlayHeader"
              >
                <div className="flex align-items-center justify-between">
                  <h4 className="w-100 lh-title font-20 md:font-32 flex pv3 pv4-lg ph5 ph7-lg">
                    {title}
                  </h4>
                </div>
              </div>
              <div className="relative">
                {parents.length > 0 ? (
                  <MediaCarousel
                    files={litterImages}
                    showArrowsOnHover
                    renderFileDetails={img =>
                      img.caption && (
                        <div className="dn db-l absolute bottom-0 ml6 mb6 white bg-black-50 br1 f2">
                          <ImageCarouselImageCaption caption={img.caption} />
                        </div>
                      )
                    }
                    dimensions={imageDimensions}
                    smartImageProps={{
                      Tag: 'div',
                      crop: true,
                      className: 'cover bg-center mw-100 min-w-100',
                      loadingClass: 'bg-black'
                    }}
                  />
                ) : (
                  <div className="bg-gray-200" style={imageDimensions} />
                )}
              </div>
              <div className="ph5 ph7-lg pv6">
                <div className="row">
                  <div className="col-md-8">
                    {description && (
                      <p className="font-16 secondary mb8">{description}</p>
                    )}

                    <BreederProfileFamilyOverlayDetailsSection title="Key Details">
                      <BreederProfileFamilyOverlayDetailsSectionItems
                        details={details}
                      />
                    </BreederProfileFamilyOverlayDetailsSection>

                    {included.length > 0 && (
                      <BreederProfileFamilyOverlayDetailsSection title="Included">
                        <BadgeList titles={included} />
                      </BreederProfileFamilyOverlayDetailsSection>
                    )}

                    <BreederProfileFamilyOverlayDetailsSection title="Pick Up & Delivery">
                      <BreederProfileDeliveryOptions
                        deliveryMethodsData={deliveryMethodsData}
                      />
                    </BreederProfileFamilyOverlayDetailsSection>
                  </div>

                  <div className="col-md-4">
                    <BreederProfileFamilyOverlayTree
                      litter={litter}
                      onPuppyClick={onPuppyClick}
                      title="Parents and Puppies"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </LoadingWrapper>
      </div>
      {isSuccess && (
        <div className="flex justify-between bg-white bt b--default ph5 ph7-sm pv2">
          {reserved || snoozed ? (
            <p className="pv3 pv4-lg ph4 fw-medium tc bw0 br3 font-16 bg-red white justify-center w-100">
              Reserved
            </p>
          ) : (
            <ModalApplyButton
              className="pv3 pv4-lg ph4 flex align-items-center justify-center w-100"
              analytics={analytics}
              breederProfileId={breederProfile.id}
              breederProfileUrl={breederProfile.profile_url}
              breedId={breedId}
              customCopy="Request info"
            />
          )}
        </div>
      )}
    </BreederProfileFamilyOverlay>
  );
}

LitterModal.propTypes = {
  litterId: PropTypes.number,
  onClose: PropTypes.func
};
